import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { useAuth } from '../contexts/AuthContext';
import { RISK_TIERS, TIER_ORDER } from '../utils/riskTier';
import { getHabRiskAllLatest, getMooringsByGridId } from '../services/mooringService';
import { getGridDiagnostics } from '../services/diagnosticsService';
import { buildDiagChips, DIAG_TONES, tileHasAlarm, NON_ADMIN_DIAG_LABELS } from '../utils/diagnostics';
import { anonymizeGrid, demoGridName, demoTown, demoBuoyLatLng } from '../utils/demo';
import { isActiveMooring, buoyNumbersById, buoyLabel } from '../utils/moorings';

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Outfit:wght@300;400;500;600;700&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DRIFT_ALERT_THRESHOLD = 150; // metres
const STALE_SYNC_MS = 2 * 60 * 60 * 1000; // 2 hours

// ── helpers ──────────────────────────────────────────────────────────────────

const severityRank = (tierKey) => TIER_ORDER.indexOf(tierKey);

// A buoy's HAB risk tier key from its diagnostics tile (per-buoy), falling back
// to the grid-level tier when the tile hasn't loaded yet.
const tileTierKey = (tile, gridTierKey) => {
  const t = tile?.hab_risk_tier;
  if (t != null && t >= 1 && t <= TIER_ORDER.length) return TIER_ORDER[t - 1];
  const label = tile?.hab_risk_label ? String(tile.hab_risk_label).toUpperCase() : null;
  if (label && RISK_TIERS[label]) return label;
  return gridTierKey || 'BASELINE';
};

const haversineMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const mooringDistM = (m) => {
  const { target_latitude: tlat, target_longitude: tlon } = m || {};
  const slat = m?.status?.latitude;
  const slon = m?.status?.longitude;
  if (tlat != null && tlon != null && slat != null && slon != null) {
    return haversineMeters(tlat, tlon, slat, slon);
  }
  return null;
};

const timeSince = (iso) => {
  if (!iso) return null;
  const mins = (Date.now() - new Date(iso)) / 60000;
  if (mins < 2)    return 'Just now';
  if (mins < 60)   return `${Math.round(mins)} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} hr ago`;
  return `${Math.round(mins / 1440)} days ago`;
};

const gridHasDriftAlert = (moorings) =>
  (moorings || []).some(m => {
    if (!isActiveMooring(m)) return false;
    const d = mooringDistM(m);
    return d != null && d >= DRIFT_ALERT_THRESHOLD;
  });

const gridHasStaleBuoy = (moorings) =>
  (moorings || []).some(m => {
    if (!isActiveMooring(m)) return false;
    return (Date.now() - new Date(m.status.latest_sync).getTime()) > STALE_SYNC_MS;
  });

// ── TrendChip ────────────────────────────────────────────────────────────────

function TrendChip({ trend }) {
  const map = {
    rising:    { label: 'Increasing', tone: 'up' },
    declining: { label: 'Decreasing', tone: 'down' },
    stable:    { label: 'Steady',     tone: 'flat' },
  };
  const t = map[trend] || map.stable;
  const colors = {
    up:   { c: '#a8500f', b: '#f6e6d6' },
    down: { c: '#2f6b46', b: '#e3efe6' },
    flat: { c: '#6f6a5f', b: '#efece3' },
  };
  const tone = colors[t.tone];
  const arrow = t.tone === 'up' ? '↑' : t.tone === 'down' ? '↓' : '→';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12.5, fontWeight: 600, color: tone.c, background: tone.b,
      padding: '4px 9px', borderRadius: 999,
    }}>
      {arrow} {t.label}
    </span>
  );
}

// ── BuoyRow ──────────────────────────────────────────────────────────────────

function BuoyRow({ mooring, tierKey, displayName, tile, isAdmin, diagLoading, onOpen }) {
  const [hover, setHover] = useState(false);
  const rk = RISK_TIERS[tierKey] || RISK_TIERS.BASELINE;
  const riskColor = RISK_TIERS[tileTierKey(tile, tierKey)]?.solid || RISK_TIERS.BASELINE.solid;
  const label = displayName || mooring?.name || mooring?.description || mooring?.label || `Buoy ${mooring?.id || ''}`;
  const latestSync = mooring?.status?.latest_sync;
  const syncedThisYear = latestSync && new Date(latestSync).getFullYear() === new Date().getFullYear();
  const isActive = mooring?.unmoored_at == null && syncedThisYear;
  const distM = mooringDistM(mooring);
  const driftAlert = isActive && distM != null && distM >= DRIFT_ALERT_THRESHOLD;
  const isStale = isActive && latestSync && (Date.now() - new Date(latestSync).getTime()) > STALE_SYNC_MS;

  const syncText = timeSince(latestSync);
  const subtitleParts = [
    syncText ? { text: `Updated ${syncText}`, stale: isStale } : null,
    isActive && distM != null ? { text: `${distM} m from anchor`, stale: driftAlert } : null,
  ].filter(Boolean);

  const chip = (chipLabel, value, alert = false) => (
    <span key={chipLabel} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontWeight: 600,
      padding: '3px 9px', borderRadius: 7,
      color: alert ? '#9e2b25' : '#5c5648',
      background: alert ? '#f7e3e0' : '#f4f1e9',
      border: `1px solid ${alert ? '#eac3bd' : '#ece7db'}`,
    }}>
      <span style={{ color: alert ? '#9e2b25' : '#a59d8b', fontWeight: 600 }}>{chipLabel}</span>
      {value}
    </span>
  );

  // Each buoy links to its own reading on the grid dashboard. Without this the
  // whole card resolves to one URL, so every buoy in a multi-buoy grid opened the
  // same (first-returned) buoy's data.
  const open = onOpen && mooring ? (e) => { e.stopPropagation(); onOpen(mooring.id); } : undefined;

  return (
    <div
      role={open ? 'button' : undefined}
      tabIndex={open ? 0 : undefined}
      onClick={open}
      onKeyDown={open ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(e); } } : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        // Padding/borders deliberately unchanged from the non-clickable row: the
        // hover tint stays inside the row's own box, since a negative-margin bleed
        // would widen the divider lines and add a horizontal scrollbar inside the
        // overflow container used for grids with more than three buoys.
        padding: '11px 0', borderTop: '1px solid #f1ede3',
        cursor: open ? 'pointer' : undefined,
        outline: 'none',
        background: open && hover ? '#faf8f3' : 'transparent',
        transition: 'background .12s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{
            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
            background: isActive ? riskColor : '#b8b1a2',
            boxShadow: isActive ? `0 0 0 3px ${riskColor}2e` : 'none',
          }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1f2a2e' }}>{label}</span>
        </div>
        {driftAlert && (
          <span style={{ fontSize: 12, fontWeight: 700, color: '#9e2b25', background: '#f7e3e0', border: '1px solid #eac3bd', padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
            ⚠ Drift
          </span>
        )}
        {/* TODO: re-enable once risk tier is wired up from API */}
        {false && !driftAlert && (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: rk.color, background: rk.bg, border: `1px solid ${rk.border}`, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
            {rk.label}
          </span>
        )}
      </div>
      {subtitleParts.length > 0 && (
        <div style={{ fontSize: 13, color: '#8b8475', marginTop: 3, paddingLeft: 19 }}>
          {subtitleParts.map((p, i) => (
            <span key={i}>
              {i > 0 && <span> · </span>}
              <span style={p.stale ? { color: '#dc2626', fontWeight: 600 } : undefined}>{p.text}</span>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', paddingLeft: 19 }}>
        {chip('Status', isActive ? 'Active' : 'Inactive')}
        {isActive && distM != null && chip('Distance', `${distM} m`, driftAlert)}
      </div>

      {/* Diagnostics — active buoys only, always open, color-coded chips. All
          users see a limited subset; admins see the full set + the ADMIN tag.
          The DIAGNOSTICS header stays up (with a spinner below) while this grid's
          diagnostics are still loading, rather than showing an empty gap. */}
      {isActive && (tile || diagLoading) && (
        <div style={{ paddingLeft: 19, marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 11 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.12em', color: '#a7a293' }}>DIAGNOSTICS</span>
            {isAdmin && (
              <span style={{ background: '#e7f4ec', color: '#1c7d45', borderRadius: 5, padding: '2px 7px', fontSize: 10, fontWeight: 700, letterSpacing: '.06em' }}>ADMIN</span>
            )}
          </div>
          {tile ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {buildDiagChips(tile)
                .filter(c => isAdmin || NON_ADMIN_DIAG_LABELS.includes(c.label))
                .map((c, i) => {
                  const tone = DIAG_TONES[c.tone] || DIAG_TONES.default;
                  return (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, padding: '8px 12px', borderRadius: 8, background: tone.bg, fontSize: 13, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                      <span style={{ color: tone.label, fontWeight: 500 }}>{c.label}</span>
                      <span style={{ color: tone.val, fontWeight: 700 }}>{c.value}</span>
                    </span>
                  );
                })}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: '#8b8475', fontSize: 13 }}>
              <span style={{ width: 15, height: 15, borderRadius: '50%', border: '2px solid #e2ddd0', borderTopColor: '#46708a', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
              Loading diagnostics…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── GridCard ─────────────────────────────────────────────────────────────────

function GridCard({ grid, tierKey, moorings, onOpen, isAttention, diagnostics, isAdmin, demoMode }) {
  const [hover, setHover] = useState(false);
  const rk = RISK_TIERS[tierKey] || RISK_TIERS.BASELINE;
  const town = [grid.city, grid.region].filter(Boolean).join(', ');

  const sortedMoorings = moorings
    ? [...moorings].sort((a, b) => {
        const now = new Date().getFullYear();
        const aAct = a.unmoored_at == null && a.status?.latest_sync && new Date(a.status.latest_sync).getFullYear() === now;
        const bAct = b.unmoored_at == null && b.status?.latest_sync && new Date(b.status.latest_sync).getFullYear() === now;
        return (bAct ? 1 : 0) - (aAct ? 1 : 0);
      })
    : undefined;

  // Number buoys per grid oldest→newest (by deployment date, then id) so labels
  // read "[Grid Name] Buoy 1/2/3" regardless of the active-first display order.
  const buoyNumberById = buoyNumbersById(moorings);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(grid.id)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(grid.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: '#fff',
        borderRadius: 14,
        borderTop: '1px solid #ece7db',
        borderRight: '1px solid #ece7db',
        borderBottom: '1px solid #ece7db',
        borderLeft: isAttention ? '4px solid #bd3a32' : '1px solid #ece7db',
        boxShadow: hover ? '0 8px 24px rgba(31,42,46,0.10)' : '0 1px 3px rgba(31,42,46,0.05)',
        transition: 'box-shadow .15s, transform .15s',
        transform: hover ? 'translateY(-2px)' : 'none',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
        outline: 'none',
        textAlign: 'left',
      }}
    >
      {/* TODO: re-enable status row once API risk tier + trend are wired up */}
      {false && (
        <div style={{ padding: '14px 18px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12.5, fontWeight: 600, color: rk.color,
            background: rk.bg, border: `1px solid ${rk.border}`,
            borderRadius: 999, padding: '4px 10px',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: rk.solid, flexShrink: 0 }} />
            {rk.label} · {rk.plain}
          </span>
          <TrendChip trend="stable" />
        </div>
      )}

      {false && <div style={{ height: 1, background: '#f4f1e9', margin: '0 18px' }} />}

      {/* Lake name + location */}
      <div style={{ padding: '14px 18px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 20, fontWeight: 600, color: '#1f2a2e', margin: 0, letterSpacing: '-0.01em',
          }}>
            {grid.name}
          </h3>
          {grid.qualifier && (
            <span style={{ fontSize: 13.5, color: '#9a9384', fontWeight: 500 }}>{grid.qualifier}</span>
          )}
        </div>
        {town && (
          <div style={{ fontSize: 13, color: '#8b8475', marginTop: 5 }}>📍 {town}</div>
        )}
      </div>

      {/* Buoys */}
      <div style={{ padding: '0 18px', flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#a59d8b' }}>
          {sortedMoorings && sortedMoorings.length > 1 ? `${sortedMoorings.length} Buoys` : 'Buoy'}
        </div>
        {sortedMoorings === undefined ? (
          <div style={{ paddingTop: 10, borderTop: '1px solid #f4f1e9' }}>
            <div style={{ height: 13, width: '55%', borderRadius: 6, background: '#f1ede3', marginBottom: 7 }} />
            <div style={{ height: 11, width: '35%', borderRadius: 6, background: '#f1ede3' }} />
          </div>
        ) : (
          // Only cap height + scroll when there are many buoys; otherwise let the
          // card grow so an expanded admin diagnostics panel doesn't overflow the
          // card and overlap the footer.
          <div style={sortedMoorings.length > 3 ? { maxHeight: 190, overflowY: 'auto' } : undefined}>
            {sortedMoorings.length > 0
              ? sortedMoorings.map(m => (
                  <BuoyRow
                    key={m.id}
                    mooring={m}
                    tierKey={tierKey}
                    displayName={`${grid.name}: ${buoyLabel(m, buoyNumberById[m.id], demoMode)}`}
                    tile={diagnostics?.[m.id]}
                    isAdmin={isAdmin}
                    diagLoading={diagnostics === undefined}
                    onOpen={(mooringId) => onOpen(grid.id, mooringId)}
                  />
                ))
              : <BuoyRow mooring={null} tierKey={tierKey} />}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '14px 18px 16px', marginTop: 8 }}>
        <span style={{
          fontSize: 14, fontWeight: 600,
          color: hover ? '#1f2a2e' : '#46708a',
          display: 'inline-flex', alignItems: 'center', gap: 4,
          transition: 'color .12s',
        }}>
          View dashboard ›
        </span>
      </div>
    </div>
  );
}

// ── DeploymentMap ─────────────────────────────────────────────────────────────

const LEGEND = [
  { label: 'Warning',  color: RISK_TIERS.WARNING.solid },
  { label: 'Advisory', color: RISK_TIERS.ADVISORY.solid },
  { label: 'Elevated', color: RISK_TIERS.ELEVATED.solid },
  { label: 'Baseline', color: RISK_TIERS.BASELINE.solid },
];

function DeploymentMap({ pondGrids, mooringsMap, habRiskMap, diagnosticsMap, isAdmin, demoMode, onOpen }) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    id: 'listing-maps-script',
  });

  const markers = useMemo(() => {
    const out = [];
    pondGrids.forEach(g => {
      const moorings = mooringsMap[g.id] || [];
      const gridTierKey = habRiskMap[g.name] || 'BASELINE';
      const now = new Date().getFullYear();
      // In demo mode, relocate pins near the demo center and swap in fake
      // grid/location labels so real partner coordinates are never exposed.
      const demoTownStr = demoMode ? [demoTown(g.id).city, demoTown(g.id).region].filter(Boolean).join(', ') : null;
      moorings
        .filter(m => m.unmoored_at == null && m.status?.latest_sync && new Date(m.status.latest_sync).getFullYear() === now)
        .forEach((m, i) => {
          const realLat = m.status?.latitude ?? m.target_latitude;
          const realLng = m.status?.longitude ?? m.target_longitude;
          if (realLat != null && realLng != null) {
            const tile = diagnosticsMap?.[g.id]?.[m.id];
            const pos = demoMode ? demoBuoyLatLng(g.id, m.id) : { lat: Number(realLat), lng: Number(realLng) };
            out.push({
              id: m.id,
              gridId: g.id,
              lat: pos.lat, lng: pos.lng,
              // Per-buoy risk from its diagnostics tile, falling back to grid tier.
              tierKey: tileTierKey(tile, gridTierKey),
              gridTierKey, // grid's highest risk tier, for the info popup
              pc: tile?.pc_fluorometer ?? null, // Phycocyanin, for the info popup
              label: demoMode ? `Buoy ${i + 1}` : (m.name || m.description || `Buoy ${m.id}`),
              gridName: demoMode ? demoGridName(g.id) : g.name,
              loc: demoMode ? demoTownStr : [g.city, g.region].filter(Boolean).join(', '),
            });
          }
        });
    });
    return out;
  }, [pondGrids, mooringsMap, habRiskMap, diagnosticsMap, demoMode]);

  const onlineCount = markers.length;

  // Fallbacks used only until the map has loaded and markers are available —
  // once both are ready, fitBoundsToMarkers takes over via map.fitBounds().
  const center = useMemo(() => {
    if (!markers.length) return { lat: 41.7, lng: -70.3 }; // Cape Cod default
    const avgLat = markers.reduce((s, m) => s + m.lat, 0) / markers.length;
    const avgLng = markers.reduce((s, m) => s + m.lng, 0) / markers.length;
    return { lat: avgLat, lng: avgLng };
  }, [markers]);

  const mapRef = useRef(null);
  const [activeId, setActiveId] = useState(null);

  const fitBoundsToMarkers = useCallback((map) => {
    if (!markers.length || !window.google) return;
    if (markers.length === 1) {
      map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
      map.setZoom(14);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
    map.fitBounds(bounds, 48);
  }, [markers]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    fitBoundsToMarkers(map);
  }, [fitBoundsToMarkers]);

  // Re-fit whenever the marker set changes (e.g. mooring data arrives after
  // the map has already loaded) so the view covers our full deployment area
  // — now spanning multiple states — rather than a fixed zoom level.
  useEffect(() => {
    if (mapRef.current) fitBoundsToMarkers(mapRef.current);
  }, [fitBoundsToMarkers]);

  const pinIcon = (color) => {
    if (!isLoaded || !window.google) return undefined;
    return {
      path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#fff',
      strokeWeight: 1.5,
      scale: 1.6,
      anchor: new window.google.maps.Point(12, 22),
    };
  };

  return (
    <div style={{
      background: '#fff', border: '1px solid #ece7db', borderRadius: 16,
      overflow: 'hidden', marginBottom: 24,
      boxShadow: '0 1px 2px rgba(31,42,46,0.04)',
    }}>
      {/* Header */}
      <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 22, fontWeight: 600, color: '#1f2a2e', margin: 0, letterSpacing: '-0.01em',
          }}>
            Deployment map
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: '#8b8475' }}>
            {onlineCount} buoy{onlineCount !== 1 ? 's' : ''} online
            {' · '}{pondGrids.length} grid{pondGrids.length !== 1 ? 's' : ''}
          </p>
        </div>
        {isAdmin && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12.5, fontWeight: 700, color: '#6f6a5f',
            background: '#f4f1e9', border: '1px solid #e3ddcf',
            padding: '6px 13px', borderRadius: 999,
          }}>
            🛡 Admin view
          </span>
        )}
      </div>

      {/* Map */}
      <div style={{ height: 420, position: 'relative', background: '#dde8e4' }}>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={11}
            onLoad={onMapLoad}
            options={{
              disableDefaultUI: false,
              mapTypeId: 'roadmap',
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: true,
              zoomControl: true,
              styles: [
                { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#b8d4dc' }] },
                { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#e8e0d0' }] },
                { featureType: 'poi', stylers: [{ visibility: 'off' }] },
              ],
            }}
          >
            {markers.map(m => {
              const color = RISK_TIERS[m.tierKey]?.solid || RISK_TIERS.BASELINE.solid;
              return (
                <Marker
                  key={m.id}
                  position={{ lat: m.lat, lng: m.lng }}
                  title={`${m.gridName} — ${m.label}`}
                  icon={pinIcon(color)}
                  onMouseOver={() => setActiveId(m.id)}
                  onMouseOut={() => setActiveId(null)}
                  onClick={() => setActiveId(m.id)}
                />
              );
            })}

            {(() => {
              const m = markers.find(x => x.id === activeId);
              if (!m) return null;
              const tier = RISK_TIERS[m.gridTierKey] || RISK_TIERS.BASELINE;
              return (
                <InfoWindow position={{ lat: m.lat, lng: m.lng }} onCloseClick={() => setActiveId(null)}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", minWidth: 150, padding: '2px 4px' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2a2e', marginBottom: 5 }}>{m.gridName}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 5 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: tier.solid, flexShrink: 0 }} />
                      <span style={{ color: tier.color, fontWeight: 600 }}>{tier.label}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: '#6f6a5f', marginBottom: m.loc ? 5 : 0 }}>
                      Most Recent Phycocyanin: <strong style={{ color: '#2a2e26' }}>{m.pc != null ? `${Number(m.pc.toFixed(2))} µg/L` : '—'}</strong>
                    </div>
                    {m.loc && <div style={{ fontSize: 12.5, color: '#6f6a5f' }}>📍 {m.loc}</div>}
                    <button
                      onClick={() => onOpen(m.gridId, m.id)}
                      style={{
                        marginTop: 9, padding: 0, background: 'none', border: 'none',
                        cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                        fontWeight: 700, color: '#46708a',
                      }}
                    >
                      {m.label} ›
                    </button>
                  </div>
                </InfoWindow>
              );
            })()}
          </GoogleMap>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8b8475', fontSize: 14 }}>
            Loading map…
          </div>
        )}

        {/* Legend overlay */}
        {/* TODO: re-enable once API risk tier is wired up per buoy */}
        {false && isLoaded && (
          <div style={{
            position: 'absolute', bottom: 16, left: 16,
            background: '#fff', borderRadius: 12, padding: '12px 16px',
            boxShadow: '0 2px 12px rgba(31,42,46,0.15)',
            border: '1px solid #ece7db',
            display: 'flex', flexDirection: 'column', gap: 7,
          }}>
            {LEGEND.map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#46505a', fontWeight: 500 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: l.color, flexShrink: 0 }} />
                {l.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── DemoToggle (admin only) ───────────────────────────────────────────────────

function DemoToggle({ on, onToggle }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 12,
      background: on ? '#eef4ff' : '#fff',
      border: `1px solid ${on ? '#b9cdf2' : '#e3ddcf'}`,
      borderRadius: 12, padding: '10px 14px', flexShrink: 0,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: on ? '#1d4ed8' : '#46505a' }}>
          🕶 Demo mode
          <span style={{ background: '#e7f4ec', color: '#1c7d45', borderRadius: 5, padding: '1px 6px', fontSize: 10, fontWeight: 700, letterSpacing: '.06em' }}>ADMIN</span>
        </span>
        <span style={{ fontSize: 12, color: '#8b8475' }}>
          {on ? 'Names & locations anonymized' : 'Show real names & locations'}
        </span>
      </div>
      <button
        role="switch"
        aria-checked={on}
        aria-label="Toggle demo mode"
        onClick={onToggle}
        style={{
          position: 'relative', width: 46, height: 26, borderRadius: 999,
          border: 'none', cursor: 'pointer', flexShrink: 0,
          background: on ? '#1d4ed8' : '#cdc7ba', transition: 'background .15s', padding: 0,
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: on ? 23 : 3,
          width: 20, height: 20, borderRadius: '50%', background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,.25)', transition: 'left .15s',
        }} />
      </button>
    </div>
  );
}

// ── PondsListPage ─────────────────────────────────────────────────────────────

export default function PondsListPage() {
  const navigate = useNavigate();
  const { grids, gridsLoading, gridsError, token, user, demoMode, toggleDemoMode } = useAuth();
  const isAdmin = !!(user?.is_admin);
  // Demo mode only takes effect for admins; everyone else always sees real data.
  const demoActive = isAdmin && demoMode;

  // Real grid → what the user sees. In demo mode identifiable fields are swapped
  // for stable fakes; tier/data lookups elsewhere still use the real grid.
  const displayGrid = (g) => (demoActive ? anonymizeGrid(g) : g);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [habRiskMap, setHabRiskMap] = useState({});
  const [mooringsMap, setMooringsMap] = useState({});
  const [diagnosticsMap, setDiagnosticsMap] = useState({}); // gridId → { mooringId: tile } (admin only)
  const [mapVisible, setMapVisible] = useState(false);

  const pondGrids = grids.filter(g => g.is_pond);

  useEffect(() => {
    if (!token) return;
    getHabRiskAllLatest(token)
      .then(data => {
        const results = Array.isArray(data) ? data : (data?.results || []);
        // A grid takes the HIGHEST risk tier among its buoys, so if the endpoint
        // returns a row per buoy we keep the max (not last-wins) per grid.
        const maxTier = {};
        results.forEach(r => {
          if (!r.grid_name) return;
          const t = r.risk_tier || 1;
          if (t > (maxTier[r.grid_name] || 0)) maxTier[r.grid_name] = t;
        });
        const map = {};
        Object.entries(maxTier).forEach(([name, t]) => {
          map[name] = TIER_ORDER[Math.max(0, t - 1)];
        });
        setHabRiskMap(map);
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token || !pondGrids.length) return;
    pondGrids.forEach(g => {
      getMooringsByGridId(g.id, token)
        .then(res => {
          const list = Array.isArray(res) ? res : (res?.results || []);
          setMooringsMap(prev => ({ ...prev, [g.id]: list }));
        })
        .catch(() => {
          setMooringsMap(prev => ({ ...prev, [g.id]: [] }));
        });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, grids.length]);

  useEffect(() => {
    if (pondGrids.length) console.log('[Grids]', pondGrids);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grids]);

  // Per-grid buoy diagnostics, keyed grid → { mooringId: tile }. Fetched for all
  // users (non-admins see a limited chip subset); admin-only concerns like the
  // alarm escalation are gated separately. Only for ACTIVE buoys — waits for the
  // grid's moorings to load, skips grids with no active buoy, and keeps only
  // active-mooring tiles. The ref guarantees each grid is fetched at most once.
  const diagFetchedRef = useRef(new Set());
  useEffect(() => {
    if (!token) return;
    pondGrids.forEach(g => {
      if (diagFetchedRef.current.has(g.id)) return;
      const moorings = mooringsMap[g.id];
      if (!moorings) return; // moorings not loaded yet — try again when they are
      diagFetchedRef.current.add(g.id);
      const activeIds = new Set(moorings.filter(isActiveMooring).map(m => m.id));
      if (activeIds.size === 0) {
        setDiagnosticsMap(prev => ({ ...prev, [g.id]: {} }));
        return;
      }
      getGridDiagnostics(g.id, token)
        .then(res => {
          const byMooring = {};
          (res?.tiles || []).forEach(t => {
            if (t?.mooring != null && activeIds.has(t.mooring)) byMooring[t.mooring] = t;
          });
          setDiagnosticsMap(prev => ({ ...prev, [g.id]: byMooring }));
        })
        .catch(() => setDiagnosticsMap(prev => ({ ...prev, [g.id]: {} })));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mooringsMap]);

  // Defer map mount until after initial cards paint
  useEffect(() => {
    const id = requestAnimationFrame(() => setMapVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Opening a grid, optionally pinned to one of its buoys. Without the buoy the
  // dashboard falls back to the grid's first active buoy.
  const openGrid = useCallback((gridId, mooringId) => {
    navigate(mooringId != null ? `/ponds/${gridId}?buoy=${mooringId}` : `/ponds/${gridId}`);
  }, [navigate]);

  const getTierKey = (grid) => habRiskMap[grid.name] || 'BASELINE';

  // Admin-only: any buoy in the grid with a diagnostics alarm (humidity === 100
  // or battery below threshold) escalates the grid to "Needs attention".
  const gridHasDiagAlarm = (g) =>
    Object.values(diagnosticsMap[g.id] || {}).some(tileHasAlarm);

  const needsAttention = (g) => {
    const moorings = mooringsMap[g.id] || [];
    const now = new Date().getFullYear();
    const hasActiveBuoy = moorings.some(m =>
      m.unmoored_at == null && m.status?.latest_sync && new Date(m.status.latest_sync).getFullYear() === now
    );
    if (!hasActiveBuoy) return false;
    // HAB risk is surfaced via the risk pills now, so it no longer escalates a
    // grid here — "needs attention" is for buoy hardware issues only. Diagnostics
    // alarms escalate for admins only (non-admins can't see those readings).
    return gridHasDriftAlert(moorings) || gridHasStaleBuoy(moorings) || (isAdmin && gridHasDiagAlarm(g));
  };

  const gridHasActiveBuoy = (g) => {
    const now = new Date().getFullYear();
    return (mooringsMap[g.id] || []).some(m =>
      m.unmoored_at == null && m.status?.latest_sync && new Date(m.status.latest_sync).getFullYear() === now
    );
  };

  // Bottom hypoxia: any active buoy with bottom DO (6h avg) at/below 2.5 mg/L.
  // diagnosticsMap holds active-buoy tiles only, so this is inherently active-grid.
  const gridHasBottomHypoxia = (g) =>
    Object.values(diagnosticsMap[g.id] || {}).some(t => t.bottom_do_6h_avg != null && t.bottom_do_6h_avg <= 2.5);

  const sorted = [...pondGrids].sort((a, b) => {
    // Needs attention first, then active buoys, then risk severity
    const aAttn = needsAttention(a) ? 1 : 0;
    const bAttn = needsAttention(b) ? 1 : 0;
    if (bAttn !== aAttn) return bAttn - aAttn;
    const aActive = gridHasActiveBuoy(a) ? 1 : 0;
    const bActive = gridHasActiveBuoy(b) ? 1 : 0;
    if (bActive !== aActive) return bActive - aActive;
    const aDrift = gridHasDriftAlert(mooringsMap[a.id]) ? 1 : 0;
    const bDrift = gridHasDriftAlert(mooringsMap[b.id]) ? 1 : 0;
    const rankDiff = severityRank(getTierKey(b)) - severityRank(getTierKey(a));
    return rankDiff !== 0 ? rankDiff : bDrift - aDrift;
  });

  const count = (fn) => pondGrids.filter(fn).length;
  const attentionCount = count(needsAttention);

  const matchFilter = (g) => {
    if (filter === 'all') return true;
    if (filter === 'attention') return needsAttention(g);
    if (filter === 'hypoxia') return gridHasBottomHypoxia(g);
    return getTierKey(g) === filter; // filter is a tier key: BASELINE/ELEVATED/ADVISORY/WARNING
  };

  const visible = sorted.filter(g => {
    const dg = displayGrid(g); // search against what the user actually sees
    const matchQ = !query || `${dg.name} ${dg.qualifier || ''} ${dg.city || ''} ${dg.region || ''}`
      .toLowerCase().includes(query.toLowerCase());
    return matchQ && matchFilter(g);
  });

  const pillStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '9px 15px', borderRadius: 999,
    border: active ? '1px solid #1f2a2e' : '1px solid #e3ddcf',
    background: active ? '#1f2a2e' : '#fff',
    color: active ? '#fff' : '#46505a',
    fontSize: 14.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all .12s',
  });

  const statusPills = [
    { key: 'all',       label: 'All lakes',        n: pondGrids.length },
    { key: 'attention', label: 'Buoy needs attention',  n: attentionCount },
    // Bottom hypoxia is derived from admin-only diagnostics, so only admins see it.
    ...(isAdmin ? [{ key: 'hypoxia', label: 'Bottom hypoxia', n: count(gridHasBottomHypoxia) }] : []),
  ];
  // One pill per HAB risk tier; a grid is placed by its tier (= its highest-risk buoy).
  const riskPills = TIER_ORDER.map(tier => ({
    key: tier,
    label: RISK_TIERS[tier].label,
    n: count(g => getTierKey(g) === tier),
  }));

  if (gridsLoading) {
    return (
      <div style={{ paddingTop: 64, minHeight: '100vh', background: '#f6f5f1', fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{GLOBAL_STYLES}</style>
        <p style={{ color: '#8b8475', fontSize: 15 }}>Loading lakes…</p>
      </div>
    );
  }

  if (gridsError) {
    return (
      <div style={{ paddingTop: 64, padding: 32, minHeight: '100vh', background: '#f6f5f1', fontFamily: "'Outfit', sans-serif" }}>
        <style>{GLOBAL_STYLES}</style>
        <div style={{ background: '#f7e3e0', border: '1px solid #eac3bd', borderRadius: 12, padding: '16px 20px', color: '#9e2b25' }}>
          Error loading lakes: {gridsError}
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 64, minHeight: '100vh', background: '#f6f5f1', fontFamily: "'Outfit', sans-serif" }}>
      <style>{GLOBAL_STYLES}</style>
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '34px 28px 64px' }}>

        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: 36, fontWeight: 600, color: '#1f2a2e', margin: 0, letterSpacing: '-0.02em',
            }}>
              Lake Monitoring
            </h1>
            <p style={{ margin: '8px 0 0', color: '#6f6a5f', fontSize: 17, lineHeight: 1.5 }}>
              Real-time water-safety conditions across your monitored lakes. Select a lake for the full dashboard.
            </p>
          </div>
          {isAdmin && <DemoToggle on={demoActive} onToggle={toggleDemoMode} />}
        </div>

        {/* Attention callout */}
        {attentionCount > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: '#fbf2db', border: '1px solid #ecdcae',
            borderRadius: 12, padding: '14px 18px', marginBottom: 22,
          }}>
            <span style={{
              width: 30, height: 30, borderRadius: '50%',
              background: '#f6e6c2', color: '#8a6308',
              display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: 16,
            }}>⚠</span>
            <p style={{ margin: 0, color: '#7a5a08', fontSize: 15.5, lineHeight: 1.45 }}>
              <strong style={{ fontWeight: 700 }}>
                {attentionCount} lake{attentionCount > 1 ? 's' : ''} need{attentionCount > 1 ? '' : 's'} attention.
              </strong>{' '}
              Conditions are elevated or higher, or a buoy has drifted beyond its anchor range — review before recreating.
            </p>
          </div>
        )}

        {/* Deployment map — deferred until after cards paint */}
        {mapVisible && (
          <DeploymentMap
            pondGrids={pondGrids}
            mooringsMap={mooringsMap}
            habRiskMap={habRiskMap}
            diagnosticsMap={diagnosticsMap}
            isAdmin={isAdmin}
            demoMode={demoActive}
            onOpen={openGrid}
          />
        )}

        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, marginBottom: 20, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {statusPills.map(p => (
              <button key={p.key} onClick={() => setFilter(p.key)} style={pillStyle(filter === p.key)}>
                {p.label}
                <span style={{ fontSize: 12.5, fontWeight: 700, color: filter === p.key ? 'rgba(255,255,255,0.7)' : '#a59d8b' }}>
                  {p.n}
                </span>
              </button>
            ))}
            <span style={{ width: 1, height: 24, background: '#e3ddcf', margin: '0 4px' }} />
            <span style={{ fontSize: 14.5, fontWeight: 700, color: '#6f6a5f' }}>HAB Risk:</span>
            {riskPills.map(p => (
              <button key={p.key} onClick={() => setFilter(p.key)} style={pillStyle(filter === p.key)}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: RISK_TIERS[p.key]?.solid, flexShrink: 0 }} />
                {p.label}
                <span style={{ fontSize: 12.5, fontWeight: 700, color: filter === p.key ? 'rgba(255,255,255,0.7)' : '#a59d8b' }}>
                  {p.n}
                </span>
              </button>
            ))}
          </div>
          <div style={{ position: 'relative', minWidth: 220, flex: '0 1 240px' }}>
            <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#a59d8b', fontSize: 16 }}>
              🔍
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search lakes…"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '11px 14px 11px 40px', fontSize: 15, borderRadius: 11,
                border: '1px solid #e3ddcf', background: '#fff',
                outline: 'none', fontFamily: 'inherit', color: '#1f2a2e',
              }}
            />
          </div>
        </div>

        {/* Grid cards */}
        {visible.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#8b8475', background: '#fff', border: '1px solid #ece7db', borderRadius: 14 }}>
            No lakes match your search.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(348px, 1fr))', gap: 18 }}>
            {visible.map(g => (
              <GridCard
                key={g.id}
                grid={displayGrid(g)}
                tierKey={getTierKey(g)}
                moorings={mooringsMap[g.id]}
                onOpen={openGrid}
                isAttention={needsAttention(g)}
                diagnostics={diagnosticsMap[g.id]}
                isAdmin={isAdmin}
                demoMode={demoActive}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
